import { useResponsive, Iconify, isPwa } from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import { FormattedMessage, useIntl } from 'react-intl';

import { useOrganizationContext } from '../../context/OrganizationProvider';
import { useNavigationContext } from '../../context/NavigationProvider';
import { NotificationsCenter } from '../NotificationsCenter';
import { Breadcrumbs } from '../../router/components';
import { useAuthContext } from '../../context/AuthProvider';
import SettingsDropdown from '../SettingsDropdown';
import { LazyChats } from './LazyChats';
import messages from './messages';

// The chat feature carries the Twilio Conversations SDK and the whole
// `components/Chats` subtree — weight that nothing on a first paint needs,
// while this header renders on every authenticated screen. It is therefore
// loaded lazily, by `LazyChats` (which also owns the error boundary, the
// stale-chunk recovery and the retry a lazy chunk needs). The availability
// gate below stays HERE, outside that boundary: a gate inside the lazy
// component would run only after the chunk had already been downloaded.
// Pinned by `AppHeader.lazy-chats.spec.tsx` and `LazyChats.spec.tsx`.

const AppHeader = () => {
  const { formatMessage } = useIntl();
  const { currentUser } = useAuthContext();
  const { currentOrganization } = useOrganizationContext();
  const { setNavigationOpen } = useNavigationContext();
  const isMobile = useResponsive('down', 'sm');
  if (currentUser?.status !== 'ACTIVE') return null;

  const onboardingDone = currentUser?.hasFinishedOnboarding;
  const isPlatformUser = Boolean(currentUser.isPlatformUser);
  const adminColor = currentUser.isSuperAdmin ? 'secondary' : 'info';
  // The same condition `allFeatures/Chats/Chats.tsx` already applied, hoisted so
  // it can decide whether the chunk is fetched at all; that component keeps its
  // own copy as the authority for what it renders.
  const isChatAvailable = Boolean(onboardingDone && currentOrganization?.id);

  return (
    <Box
      justifyContent="space-between"
      bgcolor="background.default"
      alignItems="center"
      display="flex"
      minHeight={71}
      width="100%"
      height={isPlatformUser ? 'auto' : 71}
      flexWrap={isPlatformUser ? 'wrap' : 'nowrap'}
      flexShrink={0}
      rowGap={isPlatformUser ? 1 : 0}
      py={isPlatformUser ? 1 : 0}
    >
      <Box
        flexBasis={isPlatformUser && isMobile ? '100%' : undefined}
        // on a phone the menu button leads the bar, at the screen's edge
        ml={isMobile ? 1 : 3.5}
        alignItems="center"
        display="flex"
        gap={1}
        minWidth={0}
      >
        {isMobile && (
          <Box sx={{ visibility: onboardingDone ? 'visible' : 'hidden' }}>
            <IconButton
              aria-label={formatMessage(messages.openNavigation)}
              onClick={() => setNavigationOpen(true)}
              data-testid="app-header-open-sidebar"
              edge="start"
            >
              <Iconify icon="mdi:menu" size="medium" />
            </IconButton>
          </Box>
        )}
        {isPlatformUser && (
          <Box
            data-testid="app-header-platform-role"
            bgcolor={`${adminColor}.main`}
            color={`${adminColor}.contrastText`}
            alignItems="center"
            display="inline-flex"
            whiteSpace="nowrap"
            borderRadius={1}
            fontWeight={700}
            fontSize={12}
            flexShrink={0}
            px={1}
            py={0.5}
            gap={0.75}
          >
            <Iconify
              icon={
                currentUser.isSuperAdmin
                  ? 'mdi:shield-star-outline'
                  : 'mdi:shield-account-outline'
              }
              size="small"
            />
            <FormattedMessage
              {...(currentUser.isSuperAdmin
                ? messages.superAdmin
                : messages.platformAdmin)}
            />
          </Box>
        )}
        {!isMobile && <Breadcrumbs qa="app-header__breadcrumbs" />}
      </Box>
      <Box
        flexWrap={isPlatformUser ? 'wrap' : 'nowrap'}
        maxWidth={isPlatformUser ? '100%' : undefined}
        justifyContent="flex-end"
        alignItems="center"
        display="flex"
        gap={1}
        flexShrink={0}
        ml="auto"
      >
        {isPwa() && (
          <IconButton
            onClick={() => window.location.reload()}
            data-testid="app-header-pwa-reload"
          >
            <Iconify icon="mdi:cloud-refresh-variant-outline" size="large" />
          </IconButton>
        )}
        {isChatAvailable && <LazyChats />}
        <NotificationsCenter />
        <SettingsDropdown />
      </Box>
    </Box>
  );
};

export default AppHeader;
